import React from 'react';
import { useRouter } from 'next/router';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { Badge, Typography } from '@mui/material';
import { CompletedIcon, ContinueIcon, PlayCircleIcon } from './CustomIcons';
import { KitCard, KitCardBody, KitButton } from 'components/common/cms-kit';
import { hasNewModuleNotStarted } from '../../services/gatedResourceUtils';
import hex2rgba from 'services/colorUtils/hex2rgba';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import { headingColor } from 'services/styleUtils/headingStyleUtils';

const SubscriberModuleCard = (props) => {
  const { module, hasAccess } = props;

  const router = useRouter();
  const theme = useTheme();

  return (
    <KitCard style={{ width: '21rem' }}>
      <React.Fragment>
        <KitCardBody style={{ padding: 0 }}>
          <CardHeaderContainer cardHeaderColor={props?.cardHeaderColor}>
            <DecorativeTab
              cardHeaderTabColor={props?.cardHeaderTabColor}
            ></DecorativeTab>
            <ModuleName cardHeaderTextColor={props?.cardHeaderTextColor}>
              {module.fields?.moduleName}
            </ModuleName>
          </CardHeaderContainer>
          <CardBodyContainer>
            {/* View Modules Button Only; If no module is updated or none are new or all have been started. */}
            {module.fields.comingSoon !== 'true' &&
              module?.subMods?.length > 0 &&
              module?.url &&
              !module.hasNewVersion &&
              (!module.isNew || hasNewModuleNotStarted(module) === 0) && (
                <KitButton
                  size="sm"
                  round
                  onClick={() => router.push(module.url)}
                  style={{
                    backgroundColor: buttonColor(theme, props?.buttonColor),
                    color: buttonColor(theme, props?.buttonTextColor),
                  }}
                >
                  {hasAccess ? 'View Modules' : 'See More'}
                </KitButton>
              )}
            {/* Updated: Show if any module is updated, and none are new, or one is new, but all have been started. */}
            {module.fields.comingSoon !== 'true' &&
              module?.subMods?.length > 0 &&
              module?.url &&
              module.hasNewVersion &&
              (!module.isNew ||
                (module.isNew && hasNewModuleNotStarted(module) === 0)) && (
                <Badge
                  badgeContent={'UPDATED'}
                  color="info"
                  sx={{
                    '& span.MuiBadge-colorInfo': {
                      backgroundColor: theme.palette.info.light,
                    },
                    '& span.MuiBadge-standard': {
                      borderRadius: '5px',
                      width: '80px',
                      borderLeft: '2px solid #fff',
                      borderTop: '2px solid #fff',
                      borderBottom: '2px solid #fff',
                      paddingRight: '10px',
                    },
                    '& span.MuiBadge-standard::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 0,
                      height: 0,
                      borderLeft: '16px solid transparent',
                      borderTop: '16px solid #fff',
                      clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
                      marginRight: '-1px',
                    },
                  }}
                >
                  <KitButton
                    size="sm"
                    round
                    onClick={() => router.push(module.url)}
                    style={{
                      backgroundColor: buttonColor(theme, props?.buttonColor),
                      color: buttonColor(theme, props?.buttonTextColor),
                    }}
                  >
                    {hasAccess ? 'View Modules' : 'See More'}
                  </KitButton>
                </Badge>
              )}
            {/* New: If any module is new and at least one has not been started. */}
            {module.fields.comingSoon !== 'true' &&
              module?.subMods?.length > 0 &&
              module?.url &&
              module.isNew &&
              hasNewModuleNotStarted(module) > 0 && (
                <Badge
                  badgeContent={'NEW'}
                  color="info"
                  sx={{
                    '& span.MuiBadge-colorInfo': {
                      backgroundColor: theme.palette.info.light,
                    },
                    '& span.MuiBadge-standard': {
                      borderRadius: '5px',
                      width: '80px',
                      borderLeft: '2px solid #fff',
                      borderTop: '2px solid #fff',
                      borderBottom: '2px solid #fff',
                      paddingRight: '10px',
                    },
                    '& span.MuiBadge-standard::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 0,
                      height: 0,
                      borderLeft: '16px solid transparent',
                      borderTop: '16px solid #fff',
                      clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
                      marginRight: '-1px',
                    },
                  }}
                >
                  <KitButton
                    size="sm"
                    round
                    onClick={() => router.push(module.url)}
                    style={{
                      backgroundColor: buttonColor(theme, props?.buttonColor),
                      color: buttonColor(theme, props?.buttonTextColor),
                    }}
                  >
                    {hasAccess ? 'View Modules' : 'See More'}
                  </KitButton>
                </Badge>
              )}
            {(module.fields.comingSoon === 'true' ||
              module.subMods.length === 0) && (
              <Typography
                style={{ marginTop: 9 }}
                sx={{ color: theme.palette.brandedAqua.main }}
              >
                Coming Soon
              </Typography>
            )}
          </CardBodyContainer>
          {module.fields.comingSoon !== 'true' && (
            <StateDashed>
              <StateContainer onClick={() => router.push(module.url)}>
                <StateIcon>
                  {module.state === 'Start' ? (
                    <PlayCircleIcon
                      onClick={() => router.push(module.url)}
                      sx={{
                        color: theme.palette.tertiary.main,
                        height: '1.15em',
                        width: '1.15em',
                      }}
                    />
                  ) : module.state === 'Continue' ? (
                    <ContinueIcon
                      onClick={() => router.push(module.url)}
                      sx={{
                        color: '#F2A900',
                        height: '1.25em',
                        width: '1.25em',
                      }}
                    />
                  ) : (
                    <CompletedIcon
                      onClick={() => router.push(module.url)}
                      sx={{
                        color: '#B7BF10',
                        height: '1.25em',
                        width: '1.25em',
                      }}
                    />
                  )}
                </StateIcon>
                <StateText>{module.state}</StateText>
              </StateContainer>
            </StateDashed>
          )}
          {module.fields.comingSoon === 'true' && (
            <div style={{ marginTop: 12 }}></div>
          )}
        </KitCardBody>
      </React.Fragment>
    </KitCard>
  );
};
export default SubscriberModuleCard;

const StateDashed = styled('div')`
  border-top: 1px dashed #000;
  margin: 0;
  padding-top: 0;
`;
const StateContainer = styled('div')`
  width: 75%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-content: center;
  margin: 5px 15px 15px 15px;
  padding-top: 10px;
  cursor: pointer;
`;
const StateIcon = styled('div')`
  display: inline-block;
  margin: 0 5px 0 0;
`;
const StateText = styled('div')`
  display: inline-block;
  font-size: 0.813rem;
`;
const CardHeaderContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'cardHeaderColor',
})(({ theme, cardHeaderColor }) => ({
  margin: '0 0 5px 0',
  backgroundColor: hex2rgba(headingColor(theme, cardHeaderColor), 0.1),
  padding: '0 10px 0 20px',
  height: '70px',
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
}));
const ModuleName = styled('div', {
  shouldForwardProp: (prop) => prop !== 'cardHeaderTextColor',
})(({ theme, cardHeaderTextColor }) => ({
  color: buttonColor(theme, cardHeaderTextColor),
  backgroundColor: 'transparent',
  fontSize: '1.063rem',
  lineHeight: '1.313rem',
  fontWeight: 600,
  position: 'relative',
  zIndex: 100,
}));
const DecorativeTab = styled('div', {
  shouldForwardProp: (prop) => prop !== 'cardHeaderTabColor',
})(({ theme, cardHeaderTabColor }) => ({
  display: 'inline-block',
  backgroundColor: buttonColor(theme, cardHeaderTabColor),
  position: 'absolute',
  left: '-10px',
  height: '45px',
  width: '10px',
  borderRadius: '6px 0 0 6px',
}));

const _ImageContainer = styled('div')(({ theme: _theme }) => ({
  width: '100%',
  height: '104px',
  backgroundSize: '100%',
  backgroundPosition: 'right 30% top 35%',
  backgroundRepeat: 'no-repeat',
}));

const CardBodyContainer = styled('div')(({ theme: _theme }) => ({
  padding: '10px',
  minHeight: '50px',
}));
